import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Check, Layers, Play, Trash2, HardDrive, Upload, X } from 'lucide-react'
import { useSeed } from '../hooks/useSeeds'
import { useShares } from '../hooks/useShares'
import { PruneVersionsDialog } from '../components/PruneVersionsDialog'
import { JsonViewer } from '@/components/JsonViewer'
import { appLabel } from '@/lib/app-label'
import { GfbundleApps } from '@/components/GfbundleApps'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SeedVersion } from '../types'
import { D1FilteredTable } from '@/components/D1FilteredTable'
import { R2ScopedBrowser } from '@/components/R2ScopedBrowser'
import { R2ObjectContentViewer } from '@/components/R2ObjectContentViewer'
import { useR2List } from '../hooks/useR2Browser'

const statusColors: Record<string, string> = {
  planted: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  sprouted: 'bg-green-100 text-green-800 hover:bg-green-100',
  growing: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
}

const statusLabels: Record<string, string> = {
  planted: 'Planted',
  sprouted: 'Sprouted',
  growing: 'Growing',
}

function useSeedVersions(seedName: string) {
  const [versions, setVersions] = useState<SeedVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    if (!seedName) { setLoading(false); return }
    setLoading(true)
    fetch(`/api/seeds/${seedName}/versions`)
      .then(res => res.ok ? res.json() : { versions: [] })
      .then(data => setVersions(data.versions || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [seedName, fetchKey])

  const refetch = useCallback(() => setFetchKey(k => k + 1), [])

  return { versions, loading, refetch }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function useSeedFiles(seedName: string, version: string | null) {
  const [files, setFiles] = useState<{ key: string; size: number; uploaded: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    if (!seedName || !version) { setFiles([]); return }
    setLoading(true)
    fetch(`/api/seeds/${seedName}/files?version=${version}`)
      .then(res => res.ok ? res.json() : { files: [] })
      .then(data => setFiles(data.files || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [seedName, version, fetchKey])

  const refetch = useCallback(() => setFetchKey(k => k + 1), [])

  return { files, loading, refetch }
}

interface VersionActionsProps {
  version: SeedVersion | null
  onAction: (action: 'deploy' | 'decommission' | 'purge') => void
}

function VersionActionButtons({ version, onAction }: VersionActionsProps) {
  if (!version) return null
  return (
    <div className="flex items-center gap-1">
      {!version.deployed && (
        <>
          <Button variant="outline" size="sm" onClick={() => onAction('deploy')} title="Deploy">
            <Play className="size-3.5" /> Deploy
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => onAction('purge')}
            title="Purge"
          >
            <Trash2 className="size-3.5" /> Purge
          </Button>
        </>
      )}
    </div>
  )
}

export function SeedDetail() {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()
  const { seed, loading, error } = useSeed(name!)
  const { shares } = useShares({ seed: name })
  const { versions, loading: versionsLoading, refetch: refetchVersions } = useSeedVersions(name!)

  const [selectedVersion, setSelectedVersion] = useState<string | null>(null)
  useEffect(() => {
    if (selectedVersion) return
    const current = versions.find(v => v.isCurrent)?.version ?? versions[0]?.version ?? seed?.version
    if (current) setSelectedVersion(current)
  }, [versions, seed?.version, selectedVersion])

  const activeVersionRow = useMemo(
    () => versions.find(v => v.version === selectedVersion) ?? null,
    [versions, selectedVersion],
  )
  // The Gfbundle column appears only for a Seed that has named one in some version, so
  // the version table of every other Seed is unchanged.
  const hasGfbundle = useMemo(() => versions.some(v => v.gfbundle), [versions])

  const [versionAction, setVersionAction] = useState<{ version: SeedVersion; action: 'deploy' | 'decommission' | 'withdraw' | 'expose' | 'purge' } | null>(null)
  const [versionActionError, setVersionActionError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [pruneOpen, setPruneOpen] = useState(false)
  const [fileUploading, setFileUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dataPreview, setDataPreview] = useState<{ version: string; data: unknown } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteForce, setDeleteForce] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  // The version dropdown also drives the R2/Migrations tabs, so files are
  // looked up against the same selectedVersion (no separate filesVersion).
  const { files, loading: filesLoading, refetch: refetchFiles } = useSeedFiles(name!, selectedVersion)
  const activeShares = shares.filter(s => !s.expired)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedVersion || !name) return
    setFileUploading(true)
    try {
      const body = await file.arrayBuffer()
      await fetch(`/api/seeds/${name}/files?version=${selectedVersion}&key=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        body,
      })
      refetchFiles()
    } catch { /* ignore */ }
    setFileUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileDelete = async (key: string) => {
    if (!selectedVersion || !name) return
    await fetch(`/api/seeds/${name}/files?version=${selectedVersion}&key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
    })
    refetchFiles()
  }

  const handleVersionAction = async () => {
    if (!versionAction || !name) return
    setActionLoading(true)
    setVersionActionError(null)
    try {
      const res = await fetch(`/api/seeds/${name}/versions/${versionAction.version.version}/${versionAction.action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (res.ok) {
        refetchVersions()
        setVersionAction(null)
      } else {
        const body = await res.json().catch(() => ({})) as { error?: string; pinnedShares?: number }
        const msg = body.pinnedShares
          ? `${body.error} (${body.pinnedShares} share${body.pinnedShares === 1 ? '' : 's'})`
          : body.error || `Request failed (${res.status})`
        setVersionActionError(msg)
      }
    } catch (err) {
      setVersionActionError(String(err))
    }
    setActionLoading(false)
  }

  const handleSeedDelete = async () => {
    if (!name) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const url = `/api/seeds/${name}${deleteForce ? '?force=true' : ''}`
      const res = await fetch(url, { method: 'DELETE' })
      if (res.ok) {
        navigate('/seeds')
        return
      }
      const body = await res.json().catch(() => ({})) as { error?: string; activeShares?: number }
      if (res.status === 409 && body.activeShares !== undefined) {
        setDeleteError(`${body.error} (${body.activeShares} share${body.activeShares === 1 ? '' : 's'})`)
      } else {
        setDeleteError(body.error || `Request failed (${res.status})`)
      }
    } catch (err) {
      setDeleteError(String(err))
    }
    setDeleting(false)
  }

  const handleViewData = async (version: string) => {
    try {
      const res = await fetch(`/api/seeds/${name}/versions/${version}/data`)
      if (res.ok) {
        const json = await res.json()
        setDataPreview({ version, data: json.data })
      }
    } catch { /* ignore */ }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <Skeleton className="h-8 w-1/4 mb-4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading seed: {error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!seed) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-foreground">Seed not found</h2>
          <p className="mt-2 text-muted-foreground">
            The seed "{name}" doesn't exist or you don't have access to it.
          </p>
          <div className="mt-4">
            <Button variant="link" asChild>
              <Link to="/seeds">
                <ArrowLeft />
                Back to Seeds
              </Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const apps = (seed.apps || []).filter(a => a !== 'worker')

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/seeds">
            <ArrowLeft />
            Back to Seeds
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">{seed.displayName}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{seed.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={cn('rounded-full border-transparent text-sm', statusColors[seed.status])}
              >
                {statusLabels[seed.status]}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="h-8 border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-400 dark:border-amber-400/60 dark:hover:bg-amber-950/40"
                onClick={() => setPruneOpen(true)}
                disabled={versions.length <= 1}
                title={versions.length <= 1 ? 'No older versions to prune' : `Prune old versions (${versions.length} total)`}
              >
                <Trash2 className="size-3.5 mr-1" />
                Prune…
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => { setDeleteDialogOpen(true); setDeleteError(null); setDeleteForce(false) }}
                title="Delete this seed and all its versions, shares, and data"
              >
                <Trash2 className="size-3.5 mr-1" />
                Delete seed
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-x-5 gap-y-2 flex-wrap pt-3 text-xs text-muted-foreground">
            {seed.sdkVersion && (
              <span className="flex items-center gap-1.5">
                SDK <span className="font-mono text-foreground">{seed.sdkVersion}</span>
              </span>
            )}
            {seed.agentVersion && (
              <span className="flex items-center gap-1.5">
                Agent <span className="font-mono text-foreground">{seed.agentVersion}</span>
              </span>
            )}
            {apps.length > 0 && (
              <span className="flex items-center gap-1.5">
                Apps
                {apps.map((app) => (
                  <Badge key={app} variant="outline" className="text-[11px] px-1.5 py-0">
                    {appLabel(app, seed.appsConfig, seed.displayName)}
                  </Badge>
                ))}
              </span>
            )}
            <span>Created {new Date(seed.createdAt).toLocaleDateString()}</span>
            <span>Updated {new Date(seed.updatedAt).toLocaleDateString()}</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-4 border-t mt-4">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Version</span>
            <Select value={selectedVersion ?? ''} onValueChange={setSelectedVersion}>
              <SelectTrigger className="w-40 h-8">
                <SelectValue placeholder="Pick a version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map(v => (
                  <SelectItem key={v.version} value={v.version}>
                    v{v.version}{v.isCurrent ? ' (current)' : ''}{v.deployed ? '' : ' — idle'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeVersionRow && (
              <Badge
                variant="secondary"
                className={cn(
                  'text-[10px] px-1.5 py-0 rounded-full border-transparent',
                  activeVersionRow.deployed
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-600',
                )}
              >
                {activeVersionRow.deployed ? 'Deployed' : 'Idle'}
              </Badge>
            )}
            <VersionActionButtons
              version={activeVersionRow}
              onAction={(action) => activeVersionRow && setVersionAction({ version: activeVersionRow, action })}
            />
          </div>
        </CardHeader>

        <div className="px-6 pb-6">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="d1">D1</TabsTrigger>
              <TabsTrigger value="r2">R2</TabsTrigger>
              <TabsTrigger value="migrations">Migrations</TabsTrigger>
              <TabsTrigger value="versions">Versions</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Active shares</h3>
                  <Button variant="link" size="sm" asChild>
                    <Link to="/shares/new">Share this seed</Link>
                  </Button>
                </div>
                {activeShares.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No active shares for this seed.{' '}
                    <Link to="/shares/new" className="text-primary hover:text-primary/80">Create one</Link>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {activeShares.map(share => (
                      <Link
                        key={share.id}
                        to={`/shares/${share.id}`}
                        className="flex items-center justify-between p-3 rounded-lg border hover:border-primary transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-foreground">
                            {share.name || share.workspaces.map(w => w.name).join(', ') || 'unnamed'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {share.workspaces.length} workspace{share.workspaces.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full px-1.5 py-0 text-[10px] font-medium',
                          share.expired
                            ? 'bg-gray-100 text-gray-700'
                            : share.status === 'draft'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-emerald-100 text-emerald-700',
                        )}>
                          <span className={cn('h-1 w-1 rounded-full',
                            share.expired ? 'bg-gray-500' : share.status === 'draft' ? 'bg-gray-500' : 'bg-emerald-500',
                          )} />
                          {share.expired ? 'Expired' : share.status === 'draft' ? 'Draft' : 'Published'}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="d1" className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                D1 rows scoped to this seed{selectedVersion ? <> at version <span className="font-mono">{selectedVersion}</span></> : ''}.
                {selectedVersion && (
                  <span className="block text-[11px] mt-1">
                    Note: shares with no pinned version (tracking current) are not shown when filtering by a specific version.
                  </span>
                )}
              </p>
              <D1FilteredTable table="seeds" filterCol="name" filterVal={name!} title="seeds" />
              {selectedVersion ? (
                <>
                  <D1FilteredTable
                    table="seed_versions"
                    filters={[{ col: 'seed', val: name! }, { col: 'version', val: selectedVersion }]}
                    title={`seed_versions (v${selectedVersion})`}
                  />
                  <D1FilteredTable
                    table="shares"
                    filters={[{ col: 'seed', val: name! }, { col: 'version', val: selectedVersion }]}
                    title={`shares pinned to v${selectedVersion}`}
                  />
                </>
              ) : (
                <>
                  <D1FilteredTable
                    table="seed_versions"
                    filterCol="seed"
                    filterVal={name!}
                    title={`seed_versions (${versions.length})`}
                  />
                  <D1FilteredTable table="shares" filterCol="seed" filterVal={name!} title="shares" />
                </>
              )}
            </TabsContent>

            <TabsContent value="r2" className="mt-4 space-y-4">
              {selectedVersion ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      R2 prefix: <span className="font-mono">seeds/{name}/{selectedVersion}/</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={fileUploading}
                      >
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        {fileUploading ? 'Uploading...' : 'Upload file'}
                      </Button>
                      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
                    </div>
                  </div>
                  <R2ScopedBrowser
                    bucket="seeds"
                    basePrefix={`seeds/${name}/${selectedVersion}/`}
                    lockedToBase
                  />
                  {files.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                        <HardDrive className="h-4 w-4 text-muted-foreground" />
                        Top-level files (manage via API)
                      </h4>
                      {filesLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-1/3" />
                          <Skeleton className="h-4 w-1/4" />
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>File</TableHead>
                              <TableHead>Size</TableHead>
                              <TableHead>Uploaded</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {files.map(f => (
                              <TableRow key={f.key}>
                                <TableCell><span className="font-mono text-sm text-foreground">{f.key}</span></TableCell>
                                <TableCell><span className="text-xs text-muted-foreground">{formatBytes(f.size)}</span></TableCell>
                                <TableCell><span className="text-xs text-muted-foreground">{new Date(f.uploaded).toLocaleString()}</span></TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-7 text-destructive hover:text-destructive"
                                    onClick={() => handleFileDelete(f.key)}
                                    title="Delete file"
                                  >
                                    <X className="size-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Pick a version to browse R2.</p>
              )}
            </TabsContent>

            <TabsContent value="migrations" className="mt-4">
              {selectedVersion ? (
                <MigrationsTab seedName={name!} version={selectedVersion} />
              ) : (
                <p className="text-sm text-muted-foreground">Pick a version to view migrations.</p>
              )}
            </TabsContent>

            <TabsContent value="versions" className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-foreground">All versions</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  onClick={() => setPruneOpen(true)}
                  disabled={versions.length <= 1}
                  title={versions.length <= 1 ? 'No older versions to prune' : 'Prune old versions'}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Prune…
                </Button>
              </div>
              {versionsLoading ? (
                <div className="space-y-2"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-4 w-1/4" /></div>
              ) : versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No versions deployed yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>SDK</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Worker</TableHead>
                      {hasGfbundle && <TableHead>Gfbundle</TableHead>}
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versions.map(v => (
                      <TableRow key={v.version}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedVersion(v.version)}
                              className={cn(
                                'font-mono font-medium text-foreground hover:text-primary',
                                selectedVersion === v.version && 'text-primary',
                              )}
                            >
                              v{v.version}
                            </button>
                            {v.isCurrent && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary text-primary">
                                Current
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={cn(
                              'text-[10px] px-1.5 py-0 rounded-full border-transparent',
                              v.deployed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {v.deployed ? 'Deployed' : 'Idle'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {v.sdkVersion
                            ? <span className="text-xs text-muted-foreground font-mono">sdk {v.sdkVersion}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {v.agentVersion
                            ? <span className="text-xs text-muted-foreground font-mono">agent {v.agentVersion}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {v.workerName && <span className="text-xs text-muted-foreground font-mono">{v.workerName}</span>}
                        </TableCell>
                        {hasGfbundle && (
                          <TableCell>
                            {v.gfbundle ? (
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-x-1.5 text-xs font-mono text-muted-foreground">
                                  <Link to="/gfbundles" className="font-medium text-foreground hover:text-primary" title="gfbundle version">
                                    {v.gfbundle.version}
                                  </Link>
                                  {v.gfbundle.client && <span title="client">· {v.gfbundle.client}</span>}
                                  {v.gfbundle.cell && <span title="cell">· {v.gfbundle.cell}</span>}
                                </div>
                                <GfbundleApps apps={v.gfbundle.apps} />
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">no gfbundle</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          {v.createdAt && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(v.createdAt).toLocaleString()}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="size-7" onClick={() => handleViewData(v.version)} title="View data">
                              <Eye className="size-4" />
                            </Button>
                            {v.deployed ? (
                              <>
                                {v.ready ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-7"
                                    onClick={() => setVersionAction({ version: v, action: 'withdraw' })}
                                    title="Withdraw (drop from latest — soft rollback)"
                                  >
                                    <EyeOff className="size-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-7"
                                    onClick={() => setVersionAction({ version: v, action: 'expose' })}
                                    title="Expose (re-admit to latest)"
                                  >
                                    <Check className="size-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-7 text-destructive hover:text-destructive"
                                  onClick={() => setVersionAction({ version: v, action: 'decommission' })}
                                  title="Decommission"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-7"
                                  onClick={() => setVersionAction({ version: v, action: 'deploy' })}
                                  title="Deploy"
                                >
                                  <Play className="size-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-7 text-destructive hover:text-destructive"
                                  onClick={() => setVersionAction({ version: v, action: 'purge' })}
                                  title="Purge"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </Card>

      {/* Version action confirmation dialog */}
      <Dialog open={!!versionAction} onOpenChange={() => { setVersionAction(null); setVersionActionError(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {versionAction?.action === 'deploy' && 'Deploy version'}
              {versionAction?.action === 'decommission' && 'Decommission version'}
              {versionAction?.action === 'withdraw' && 'Withdraw version'}
              {versionAction?.action === 'expose' && 'Expose version'}
              {versionAction?.action === 'purge' && 'Purge version'}
            </DialogTitle>
            <DialogDescription>
              {versionAction?.action === 'deploy' && (
                <>Enable dispatch routing to <strong>v{versionAction.version.version}</strong>? Shares pinned to this version will start working immediately.</>
              )}
              {versionAction?.action === 'decommission' && (
                <>Stop dispatch routing to <strong>v{versionAction.version.version}</strong>? Shares pinned to this version will get a 503 error. The worker stays in the namespace and can be re-deployed later.</>
              )}
              {versionAction?.action === 'withdraw' && (
                <>Withdraw <strong>v{versionAction.version.version}</strong> from "latest"? Shares following latest roll back to the next-newest ready version. Shares pinned to this version keep working. Re-admit any time with Expose.</>
              )}
              {versionAction?.action === 'expose' && (
                <>Re-admit <strong>v{versionAction.version.version}</strong> to "latest"? Shares following latest will pick it up if it's the newest ready version.</>
              )}
              {versionAction?.action === 'purge' && (
                <>Permanently delete <strong>v{versionAction.version.version}</strong>: dispatch worker, R2 storage, and any orphaned share data. This action is irreversible.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {versionActionError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{versionActionError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVersionAction(null); setVersionActionError(null) }} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant={versionAction?.action === 'purge' ? 'destructive' : 'default'}
              onClick={handleVersionAction}
              disabled={actionLoading}
            >
              {actionLoading ? 'Processing...' : versionAction ? versionAction.action.charAt(0).toUpperCase() + versionAction.action.slice(1) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed-level delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteError(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete seed</DialogTitle>
            <DialogDescription>
              Permanently delete <strong>{seed?.displayName}</strong>: every version's dispatch worker, R2 storage under <code className="font-mono text-xs">seeds/{name}/</code> and <code className="font-mono text-xs">shares/&#123;id&#125;/</code> for each share, and all D1 rows (versions, shares, workspaces, messages). This action is irreversible.
            </DialogDescription>
          </DialogHeader>
          {activeShares.length > 0 && (
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={deleteForce}
                onChange={e => setDeleteForce(e.target.checked)}
                className="mt-1"
              />
              <span>
                Force delete — this seed has <strong>{activeShares.length}</strong> active share{activeShares.length === 1 ? '' : 's'} that will also be deleted.
              </span>
            </label>
          )}
          {deleteError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSeedDelete}
              disabled={deleting || (activeShares.length > 0 && !deleteForce)}
            >
              {deleting ? 'Deleting...' : 'Delete seed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data preview JSON viewer */}
      <JsonViewer
        open={!!dataPreview}
        onOpenChange={() => setDataPreview(null)}
        title={dataPreview ? `Version ${dataPreview.version} — Data` : 'Data'}
        data={dataPreview?.data}
      />

      <PruneVersionsDialog
        open={pruneOpen}
        onClose={() => setPruneOpen(false)}
        onComplete={() => refetchVersions()}
        seedName={name!}
        versions={versions}
      />
    </div>
  )
}

interface MigrationsTabProps {
  seedName: string
  version: string
}

function MigrationsTab({ seedName, version }: MigrationsTabProps) {
  const prefix = `seeds/${seedName}/${version}/migrations/`
  const { result, loading, error } = useR2List('seeds', prefix)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const sqlFiles = (result?.objects ?? []).filter(o => o.key.endsWith('.sql'))

  return (
    <div className="space-y-3">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          These SQL migrations hydrate each share's Durable Object on first hit. There is no live DO state at the seed level — open a share to inspect live data.
        </AlertDescription>
      </Alert>

      <R2ObjectContentViewer
        open={!!selectedKey}
        onOpenChange={(open) => { if (!open) setSelectedKey(null) }}
        bucket="seeds"
        objectKey={selectedKey}
      />

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : sqlFiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">No migration files at <span className="font-mono">{prefix}</span>.</p>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Migration</TableHead>
                <TableHead className="w-28 text-right">Size</TableHead>
                <TableHead className="w-44">Uploaded</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sqlFiles.map(f => {
                const fileName = f.key.slice(prefix.length)
                return (
                  <TableRow
                    key={f.key}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedKey(f.key)}
                  >
                    <TableCell><span className="font-mono text-sm">{fileName}</span></TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {formatBytes(f.size)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {new Date(f.lastModified).toLocaleString()}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}
